/**
 * RestoID — passkey verification (identifier sign-in flow).
 * ---------------------------------------------------------------------------
 * RestoID frame on the PRISTINE upstream passkey verification logic
 * (`useIdentifierPasskeySignInVerification`). WebAuthn options come via nav
 * state; identifier + verificationId from context. Guards mirror upstream.
 * Swapped in for `./pages/SignInPasskeyVerification` via `src/App.tsx`.
 */
import { SignInIdentifier, VerificationType } from '@logto/schemas';
import { cond } from '@silverhand/essentials';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import SwitchToVerificationMethodsLink from '@/components/SwitchToVerificationMethodsLink';
import useIdentifierPasskeySignInVerification from '@/hooks/use-identifier-passkey-sign-in-verification';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import { identifierPasskeyStateGuard } from '@/types/guard';
import { isWebAuthnOptions } from '@/utils/webauthn';

import { Button, HeroTile, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const SignInPasskeyVerification = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { state } = useLocation();
  const navigate = useNavigateWithPreservedSearchParams();
  const [, passkeyState] = validate(state, identifierPasskeyStateGuard);
  const { verificationIdsMap, identifierInputValue } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[VerificationType.SignInPasskey];
  const { signInMethods } = useSieMethods();

  const handleVerify = useIdentifierPasskeySignInVerification();
  const [isVerifying, setIsVerifying] = useState(false);

  if (!passkeyState || !verificationId || !identifierInputValue?.type) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { options } = passkeyState;
  if (!isWebAuthnOptions(options)) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = identifierInputValue;
  const methodSetting = signInMethods.find((method) => method.identifier === type);

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'action.verify_via_passkey' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('action.verify_via_passkey')}
          sub={t('mfa.verify_via_passkey_description')}
        />
        <HeroTile icon="finger" />
        <Button
          title={t('action.verify_via_passkey')}
          isLoading={isVerifying}
          onClick={async () => {
            if (isVerifying) {
              return;
            }
            setIsVerifying(true);
            try {
              await handleVerify(options, verificationId);
            } finally {
              setIsVerifying(false);
            }
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SwitchToVerificationMethodsLink
            identifier={cond(type !== SignInIdentifier.Username && type)}
            value={value}
            hasPassword={methodSetting?.password}
            hasVerificationCode={
              type !== SignInIdentifier.Username && methodSetting?.verificationCode
            }
          />
        </div>
      </Stack>
    </FirstScreenLayout>
  );
};

export default SignInPasskeyVerification;
